import dayjs from 'dayjs';
import type { KidFiguresRecordModel } from '../../../../api/kids/types';
import type { FundData } from '../../../../api/funds/types';
import type { ShareClassModel } from '../../../../api/shareclass/types';

const HOLDING_PERIOD_FIELD_KEYS = [
  'recommended_holding_period',
  'holding_period',
  'kid_holding_period',
] as const;

function readHoldingPeriodYears(
  source: Record<string, unknown> | null | undefined,
): number | null {
  if (!source) return null;

  for (const key of HOLDING_PERIOD_FIELD_KEYS) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return value;
    }
  }

  return null;
}

export function formatHoldingPeriodColumnLabel(
  holdingPeriodYears: number | null,
): string {
  if (holdingPeriodYears == null) {
    return 'Holding period';
  }

  const unit = holdingPeriodYears === 1 ? 'year' : 'years';
  return `${holdingPeriodYears} ${unit} (Recommended Holding Period)`;
}

/** Resolve recommended holding period in years from fund or share class model. */
export function resolveHoldingPeriodYears(
  fund?: FundData | null,
  shareClass?: ShareClassModel | null,
): number | null {
  return (
    readHoldingPeriodYears(fund as Record<string, unknown> | undefined) ??
    readHoldingPeriodYears(shareClass as unknown as Record<string, unknown> | undefined)
  );
}

export type KidFiguresStatus = 'none' | 'current' | 'outdated';

/** Most recent reporting Friday on or before today (inclusive when today is Friday). */
export function getLastReportingFriday(from = dayjs()) {
  const date = from.startOf('day');
  const daysSinceFriday = (date.day() + 2) % 7;
  return date.subtract(daysSinceFriday, 'day');
}

export function areKidFiguresUpToDate(
  calculationDate: string | null | undefined,
): boolean {
  if (!calculationDate) return false;

  const figureDate = dayjs(calculationDate.slice(0, 10));
  if (!figureDate.isValid()) return false;

  const lastFriday = getLastReportingFriday();
  return (
    figureDate.isSame(lastFriday, 'day') || figureDate.isAfter(lastFriday, 'day')
  );
}

export function getKidFiguresStatus(
  hasFigures: boolean,
  calculationDate: string | null | undefined,
  requiresRecalculation = false,
): KidFiguresStatus {
  if (!hasFigures) return 'none';
  if (requiresRecalculation || !areKidFiguresUpToDate(calculationDate)) {
    return 'outdated';
  }
  return 'current';
}

export const KID_FIGURES_STATUS_LABEL: Record<KidFiguresStatus, string> = {
  none: 'No figures yet',
  current: 'Figures up to date',
  outdated: 'Figures out of date',
};

export const KID_FIGURES_STATUS_CLASS: Record<KidFiguresStatus, string> = {
  none: 'bg-gray-100 text-gray-700',
  current: 'bg-green-100 text-green-800',
  outdated: 'bg-amber-100 text-amber-800',
};

export function formatKidFigureCalculationDate(
  calculationDate: string | null | undefined,
): string {
  if (!calculationDate) return '—';
  const parsed = dayjs(calculationDate.slice(0, 10));
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : calculationDate;
}

export function formatKidFigureCalculatedTimestamp(
  timestamp: string | null | undefined,
): string {
  if (!timestamp) return '—';
  const parsed = dayjs(timestamp);
  return parsed.isValid()
    ? parsed.format('YYYY-MM-DD HH:mm:ss')
    : timestamp;
}

export function pickLatestKidFigure(
  figures: KidFiguresRecordModel[] | null | undefined,
): KidFiguresRecordModel | null {
  if (!figures?.length) return null;
  return [...figures].sort(
    (a, b) =>
      new Date(b.last_updating_datetime || b.calculation_date).getTime() -
      new Date(a.last_updating_datetime || a.calculation_date).getTime(),
  )[0];
}
